import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Bed, Bath, Maximize, MapPin, Search, Loader2, Eye, Building } from 'lucide-react';
import { CurrencyConverter } from '../components/CurrencyConverter';
import { formatCurrency } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Properties = () => {
  const [typeFilter, setTypeFilter] = useState('all');
  const [bedroomFilter, setBedroomFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('GBP');
  const [dbListings, setDbListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch active listings from database
  useEffect(() => {
    const fetchActiveListings = async () => {
      try {
        // Fetch listings with 'live' status (publicly visible)
        const response = await fetch(`${API_URL}/api/listings?status=live`);
        if (response.ok) {
          const data = await response.json();
          setDbListings(data);
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
      }
      setLoading(false);
    };
    
    fetchActiveListings();
  }, []);

  // Only show real database listings
  const allProperties = dbListings.map(listing => ({
    id: listing.id,
    title: listing.title,
    location: `${listing.address}, ${listing.city}`,
    price: listing.price,
    bedrooms: listing.bedrooms,
    bathrooms: listing.bathrooms,
    area: listing.floor_area || 0,
    type: listing.listing_type,
    image: listing.images?.[0] || 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800&q=80',
    features: listing.features || [],
    description: listing.description,
    isFromDb: true
  }));

  const filteredProperties = allProperties.filter((property) => {
    const matchesType = typeFilter === 'all' || property.type === typeFilter;
    const matchesBedrooms = bedroomFilter === 'all' || property.bedrooms === parseInt(bedroomFilter);
    const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          property.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesType && matchesBedrooms && matchesSearch;
  });

  return (
    <div className="min-h-screen pt-20 pb-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-12 fade-in">
          <h1 className="text-4xl md:text-5xl font-bold text-[#404040] mb-4">
            Find Your Perfect Property
          </h1>
          <p className="text-lg text-gray-600">
            Browse our selection of properties for sale and rent in Harrow and surrounding areas
          </p>
        </div>

        {/* Search and Filter Section */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search by location or title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Property Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="sale">For Sale</SelectItem>
                  <SelectItem value="rent">To Rent</SelectItem>
                </SelectContent>
              </Select>

              <Select value={bedroomFilter} onValueChange={setBedroomFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Bedrooms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Bedrooms</SelectItem>
                  <SelectItem value="1">1 Bedroom</SelectItem>
                  <SelectItem value="2">2 Bedrooms</SelectItem>
                  <SelectItem value="3">3 Bedrooms</SelectItem>
                  <SelectItem value="4">4+ Bedrooms</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger>
                  <SelectValue placeholder="Currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GBP">🇬🇧 GBP</SelectItem>
                  <SelectItem value="KWD">🇰🇼 KWD</SelectItem>
                  <SelectItem value="IQD">🇮🇶 IQD</SelectItem>
                  <SelectItem value="SAR">🇸🇦 SAR</SelectItem>
                  <SelectItem value="AED">🇦🇪 AED</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                onClick={() => {
                  setTypeFilter('all');
                  setBedroomFilter('all');
                  setSearchTerm('');
                }}
                variant="outline"
                className="border-[#FF8C42] text-[#FF8C42] hover:bg-[#FF8C42] hover:text-white"
              >
                Reset Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-[#FF8C42]">{filteredProperties.length}</span> properties
          </p>
        </div>

        {/* Property Grid */}
        {filteredProperties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((property) => (
              <Link 
                to={property.isFromDb ? `/property/${property.id}` : '#'} 
                key={property.id}
                className="block"
              >
                <Card className="property-card-hover overflow-hidden border-none shadow-lg cursor-pointer h-full">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={property.image}
                      alt={property.title}
                      className="property-image w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white">
                        {property.type === 'sale' ? 'For Sale' : 'To Rent'}
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 left-4">
                      <div className="text-white text-2xl font-bold drop-shadow-lg">
                        {formatCurrency(property.price, selectedCurrency)}
                        {property.type === 'rent' && ' pcm'}
                      </div>
                    </div>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                      <span className="bg-white text-[#404040] px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                        <Eye className="w-4 h-4" /> View Details
                      </span>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#404040] mb-2">{property.title}</h3>
                    
                    <div className="flex items-center text-gray-600 mb-4">
                      <MapPin className="w-4 h-4 mr-1 text-[#FF8C42]" />
                      <span className="text-sm">{property.location}</span>
                    </div>

                    <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
                      <div className="flex items-center text-gray-600">
                        <Bed className="w-5 h-5 mr-1 text-[#FF8C42]" />
                        <span className="text-sm font-medium">{property.bedrooms}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Bath className="w-5 h-5 mr-1 text-[#FF8C42]" />
                        <span className="text-sm font-medium">{property.bathrooms}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Maximize className="w-5 h-5 mr-1 text-[#FF8C42]" />
                        <span className="text-sm font-medium">{property.area} sq ft</span>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {property.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {property.features.slice(0, 3).map((feature, index) => (
                        <Badge key={index} variant="outline" className="text-xs border-[#FF8C42] text-[#FF8C42]">
                          {feature}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex space-x-2">
                      <Button className="flex-1 bg-[#404040] hover:bg-[#FF8C42] text-white">
                        <Eye className="w-4 h-4 mr-2" /> View Details
                      </Button>
                      <CurrencyConverter basePrice={property.price} propertyTitle={property.title} />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center border-none shadow-lg">
            <Building className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-xl text-gray-600 mb-2">
              {allProperties.length === 0 
                ? "No properties available yet" 
                : "No properties found matching your criteria"
              }
            </p>
            <p className="text-gray-500 mb-4">
              {allProperties.length === 0 
                ? "Check back soon for new listings"
                : "Try adjusting your search filters"
              }
            </p>
            {(typeFilter !== 'all' || bedroomFilter !== 'all' || searchTerm) && (
              <Button
                onClick={() => {
                  setTypeFilter('all');
                  setBedroomFilter('all');
                  setSearchTerm('');
                }}
                className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white"
              >
                Clear All Filters
              </Button>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};
